import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import tenantConfig from '../config/tenant';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsContext = createContext(null);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

// Applies the active app font as a CSS variable. Must be used within SettingsProvider.
export const useAppFont = () => {
  const { settings } = useSettings();
  useEffect(() => {
    const font = settings.app_font || 'Outfit';
    document.documentElement.style.setProperty('--app-font', `'${font}'`);
  }, [settings.app_font]);
};

const FALLBACK = {
  show_financial_widget: false,
  widget_title: 'Transparansi Keuangan',
  mosque_name: tenantConfig.mosqueName,
  tagline: tenantConfig.tagline,
  logo_url: tenantConfig.logo,
  location_city: tenantConfig.location.city,
  location_address: tenantConfig.location.address,
  slider_images: [],
  home_menu: [
    { id: 'tasbih', label: 'Tasbih Digital', icon: 'Sparkles', color: '#10B981', path: '/tasbih' },
    { id: 'quran', label: 'Al-Quran', icon: 'Book', color: '#065F46', path: '/quran' },
    { id: 'streaming', label: 'Live Streaming', icon: 'Radio', color: '#D4AF37', path: '/streaming' },
    { id: 'warta', label: 'Warta & Program', icon: 'Calendar', color: '#022C22', path: '/warta' },
  ],
  nav_menu: [
    { id: 'home', label: 'Home', icon: 'Home', color: '#065F46', path: '/' },
    { id: 'jadwal', label: 'Jadwal', icon: 'Clock', color: '#065F46', path: '/jadwal' },
    { id: 'infaq', label: 'Infaq', icon: 'Heart', color: '#065F46', path: '/infaq' },
    { id: 'pasar', label: 'Pasar', icon: 'Store', color: '#065F46', path: '/pasar' },
  ],
  hero_image_url: null,
  greeting_text: '',
  spirit_kit_title: 'Spiritual Kit',
  app_font: 'Outfit',
  tasbih_icon: 'classic',
  pwa_logo_url: null,
  show_download_widget: true,
  download_widget_title: 'Pasang Aplikasi Masjid',
  streaming_makkah_url: 'https://www.youtube.com/embed/FQBNhNL0OWU',
  streaming_madinah_url: 'https://www.youtube.com/embed/yOFfjekGiyE',
};

export const SettingsProvider = ({ children, mosqueId, initialSettings }) => {
  const [settings, setSettings] = useState({ ...FALLBACK, ...(initialSettings || {}) });
  const [loading, setLoading] = useState(true);

  const refreshSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings`, {
        params: mosqueId ? { mosque_id: mosqueId } : {},
      });
      setSettings({ ...FALLBACK, ...data });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  }, [mosqueId]);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  const updateSettings = useCallback(async (partial) => {
    await axios.put(`${API_URL}/api/settings`, partial, {
      withCredentials: true,
      params: mosqueId ? { mosque_id: mosqueId } : {},
    });
    await refreshSettings();
  }, [refreshSettings, mosqueId]);

  return (
    <SettingsContext.Provider value={{ settings, loading, refreshSettings, updateSettings, mosqueId }}>
      {children}
    </SettingsContext.Provider>
  );
};
